import React, { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useEnglishText } from "../../text/useEnglishText.js";
import {
  BottomNavigation,
  BottomNavigationAction,
  styled,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";

// MUI Icons
import DashboardIcon from "@mui/icons-material/DashboardOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/PeopleAlt";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

const StyledBottomNavigation = styled(BottomNavigation)(() => ({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  boxShadow: "0 -4px 18px rgba(16, 24, 40, 0.08)",
  zIndex: 1300,
  backgroundColor: "var(--surface)",
  minHeight: "64px",
  height: "calc(64px + env(safe-area-inset-bottom))",
  borderRadius: "14px 14px 0 0",
  borderTop: "1px solid var(--surface-border)",
  paddingBottom: "env(safe-area-inset-bottom)",
}));

const StyledAction = styled(BottomNavigationAction)({
  color: "var(--text-secondary)",
  minWidth: "auto",
  padding: "6px 0",
  "&.Mui-selected": {
    color: "var(--primary)",
    "& .MuiSvgIcon-root": {
      transform: "scale(1.2) translateY(-2px)",
      transition: "all 0.25s ease",
    },
    "& .MuiBottomNavigationAction-label": {
      fontWeight: 700,
      fontSize: "0.75rem",
    },
  },
});

const BottomTab = () => {
  const { t } = useEnglishText();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:1024px)");

  const tabs = useMemo(() => {
    return [
      {
        key: "dashboard",
        path: `/dashboard`,
        icon: <DashboardIcon />,
        label: t("sidebar.dashboard"),
      },
      {
        key: "companies",
        path: `/dashboard/companies`,
        icon: <BusinessIcon />,
        label: t("sidebar.companies"),
      },
      {
        key: "users",
        path: `/dashboard/users`,
        icon: <PeopleIcon />,
        label: t("sidebar.users"),
      },
      {
        key: "wallet",
        path: `/dashboard/wallet`,
        icon: <AccountBalanceWalletIcon />,
        label: t("sidebar.wallet"),
      },
      {
        key: "tickets",
        path: `/dashboard/tickets`,
        icon: <SupportAgentIcon />,
        label: t("sidebar.tickets"),
      },
    ];
  }, [t]);

  const currentTabKey = useMemo(() => {
    const current = tabs.find((tab) =>
      tab.path === "/dashboard"
        ? location.pathname === tab.path
        : location.pathname.startsWith(tab.path)
    );
    return current ? current.key : "dashboard";
  }, [location.pathname, tabs]);

  if (!isMobile) return null;

  return (
    <StyledBottomNavigation
      value={currentTabKey}
      onChange={(e, val) => {
        const target = tabs.find((t) => t.key === val);
        if (target) navigate(target.path);
      }}
      showLabels
    >
      {tabs.map((tab) => (
        <StyledAction
          key={tab.key}
          value={tab.key}
          label={tab.label}
          icon={tab.icon}
        />
      ))}
    </StyledBottomNavigation>
  );
};

export default BottomTab;
